/**
 * How each match is actually scored, read off the match itself.
 *
 * Deliberately derived from the live config rather than written out as prose:
 * a rules page that is maintained by hand drifts from the engine, and the
 * moment it does it is worse than nothing, because people trust it. Every
 * number here — the quota table, the payouts, the net basis, the cap — is the
 * same object the scorer uses.
 *
 * The assumptions are spelled out because they are the parts the info sheet
 * left open, and the group is entitled to know which way they were read before
 * the money moves rather than after.
 */

import { holesFor } from '../engine/handicap'
import { matchNameIn, roundName } from '../engine/naming'
import { matchCounts } from '../engine/types'
import type { Match, Tournament } from '../engine/types'
import { matchesInPlayOrder, roundDay } from '../select'
import { Bar, Empty, SectionRule } from '../ui'

const money = (n: number) => `$${n.toLocaleString()}`

function Line({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-3 py-1 text-[13px]">
      <span className="text-mute w-28 shrink-0">{k}</span>
      <span className="flex-1">{v}</span>
    </div>
  )
}

const netBasisText = (b: string) =>
  b === 'relativeToLowInUnit'
    ? 'Off the low man in the match — he plays scratch, you get the difference.'
    : b === 'relativeToLowInGroup'
      ? 'Off the low man in the foursome, so both sides of a 2v2 use one frame.'
      : 'Full course handicap.'

/**
 * Everything about one match that decides a number.
 *
 * The SHAPE of the round is passed in rather than assumed. This page used to
 * say "up to 18 points over a nine" and "the other three in your foursome" on
 * every match, which was true of the 2025 sheet and stopped being true the
 * moment a round was eighteen holes or a group went out as a three.
 */
export function MatchRulesFor({
  doc,
  match,
}: {
  doc: Tournament
  match: Match
}) {
  const round = doc.rounds.find((r) => r.id === match.roundId)
  const course = doc.courses.find((c) => c.id === round?.courseId)
  return (
    <MatchRules
      match={match}
      holes={round && course ? holesFor(course, round.nine).length : 0}
      fieldSize={doc.players.filter((p) => p.active).length}
      groupSize={doc.groups.find((g) => g.roundId === match.roundId)?.playerIds.length ?? 4}
    />
  )
}

function MatchRules({
  match,
  holes,
  fieldSize,
  groupSize,
}: {
  match: Match
  /** Holes this round actually plays. */
  holes: number
  /** Men in the field, for a quota ranked against all of them. */
  fieldSize: number
  /** Men in a foursome on this round — not always four. */
  groupSize: number
}) {
  const c = match.config

  if (
    c.kind === 'individualMatchPlay' ||
    c.kind === 'teamCombinedNet' ||
    c.kind === 'teamFourBall'
  ) {
    const team = c.kind === 'teamCombinedNet' || c.kind === 'teamFourBall'
    return (
      <>
        <Line k="Scoring" v={
          c.kind === 'teamFourBall'
            ? 'Each man plays his own ball; the side posts its LOWER net. Best ball wins the hole, ties halved.'
            : team
              ? 'Both partners’ net scores added; the lower team total wins the hole. Ties halved.'
              : 'Low net wins the hole. Ties halved.'} />
        <Line k="Strokes" v={netBasisText(c.netBasis)} />
        <Line k="Closeout" v={c.closeout
          ? `A match ends when the lead exceeds the holes left. Over ${holes} holes, 3 up with 2 to play is final.`
          : 'Played out to the last hole regardless of the margin.'} />
        <Line k="Gimmes" v="The length of putt given is the group’s call." />
        <Line k="Win" v={`${c.payout.win.points} pts · ${money(c.payout.win.cash)}`} />
        <Line k="Halve" v={`${c.payout.halve.points} pts · ${money(c.payout.halve.cash)}`} />
        <Line k="Loss" v={`${c.payout.loss.points} pts · ${money(c.payout.loss.cash)}`} />
        <Line k="Ties" v="A halved match splits the win: both sides take half the points and half the money." />
      </>
    )
  }

  if (c.kind === 'teamHighLow') {
    return (
      <>
        <Line k="Scoring" v={`Each side's low ball and high ball are compared separately. ${c.pointsPerHole.low} pt for the low, ${c.pointsPerHole.high} for the high, ${c.tieValue} each when tied.`} />
        <Line k="Strokes" v={netBasisText(c.netBasis)} />
        <Line k="Gimmes" v="The length of putt given is the group’s call." />
        <Line k="Worth" v={`Up to ${(c.pointsPerHole.low + c.pointsPerHole.high) * holes} points over ${holes} holes.`} />
        <Line k="Win" v={`${c.payout.win.points} pts · ${money(c.payout.win.cash)} each`} />
        <Line k="Assumption" v={`Read as a point per hole for each ball, so ${holes} holes are worth ${(c.pointsPerHole.low + c.pointsPerHole.high) * holes}. The sheet's “after each hole” makes that the natural reading, but it is a reading.`} />
      </>
    )
  }

  if (c.kind === 'quota') {
    const t = c.table
    const tableText = [
      `eagle+ ${t.eagleOrBetter}`,
      `birdie ${t.birdie}`,
      `par ${t.par}`,
      `bogey ${t.bogey}`,
      `double or worse ${t.worse}`,
    ].join(' · ')
    return (
      <>
        <Line k="Target" v={`${c.quotaFormula.pointsPerPar} × ${holes} holes = ${c.quotaFormula.pointsPerPar * holes}${c.quotaFormula.subtractHandicap ? ', less your course handicap' : ''}. Beat it and you are plus.`} />
        <Line k="Points" v={tableText} />
        <Line k="Scored off" v={c.scoreBasis === 'gross'
          ? 'GROSS versus par. The handicap is already inside the target — scoring off net as well would count it twice.'
          : 'Net versus par.'} />
        <Line k="Ranked against" v={c.rankingScope === 'group'
          ? `The other ${groupSize - 1} in your group.`
          : `All ${fieldSize} in the field.`} />
        {/*
          The hole-out rule follows the RANKING SCOPE, which is the reason for
          it: a putt can only be conceded by somebody standing there, and in a
          field-ranked game the men you are beating are on other fairways.
        */}
        {c.rankingScope === 'field' ? (
          <Line
            k="Holing out"
            v={`Everything is in the hole — no gimmes. All ${fieldSize} are in this one, and the field cannot concede a putt.`}
          />
        ) : (
          <Line k="Gimmes" v="The length of putt given is the group’s call." />
        )}
        {c.payouts.map((p) => (
          <Line
            key={`${p.from}-${p.to}`}
            k={p.from === p.to ? `${p.from}${p.from === 1 ? 'st' : p.from === 2 ? 'nd' : p.from === 3 ? 'rd' : 'th'}` : `${p.from}–${p.to}`}
            v={`${p.points} ${p.points === 1 ? 'pt' : 'pts'}${p.cash ? ` · ${money(p.cash)}` : ''}`}
          />
        ))}
      </>
    )
  }

  return (
    <>
      <Line k="Scoring" v="Gross strokes for the round, less your allowance. Lowest wins." />
      {/*
        A starting figure, not pop holes. The card carries no strokes: your
        allowance comes off the total once, so you begin the round that many under
        par and play the day gross against it.
      */}
      <Line k="Strokes" v="You start at your course handicap under par. No pop holes on the card." />
      {/*
        There is no maximum. This line used to promise a net triple bogey cap and
        the format used to apply one; both went when the pops did, because a cap
        needs a per-hole allowance and there no longer is one. Stated rather than
        dropped — "no maximum" is the thing somebody wants to know standing over a
        fourth shot from the gorse.
      */}
      {/*
        Two different rules, and they were one line while only one of them
        existed. The ceiling is on a hole a man FINISHES; the posting is what a
        hole nobody finishes costs him.
      */}
      <Line
        k="Bad holes"
        v={
          c.maxOverParGross == null
            ? c.maxOverParNet == null
              ? 'No maximum. Hole out and post it.'
              : `No maximum — hole out and post it. A hole nobody finishes posts ${c.maxOverParNet} over par.`
            : `${c.maxOverParGross} over par is the most any hole can cost you${
                c.maxOverParNet == null
                  ? '.'
                  : `, and a hole nobody finishes posts ${c.maxOverParNet} over.`
              }`
        }
      />
      {c.maxOverParGross != null && (
        <Line
          k="The cap"
          v="On the gross, before anything else. There are no pop holes here and the allowance comes off the total once, so capping the net would spend it twice."
        />
      )}
      {/*
        Flighted is what makes it the championship — the whole field's money is
        on every card, so nobody standing next to you can wave a putt off. An
        unflighted card is a round for the fun of it, and gets no such sermon.
      */}
      {c.flighted && (
        <Line
          k="Holing out"
          v="Everything is in the hole — no gimmes. The whole field is on this card, and the field cannot concede a putt."
        />
      )}
      <Line k="Flights" v={c.flighted
        ? c.flights.map((f) => `${f.name} (seeds ${f.fromSeed}–${f.toSeed})`).join(', ')
        : 'One field, no flights.'} />
      {c.flights.flatMap((f) =>
        f.payouts.map((p) => (
          <Line
            key={`${f.name}-${p.from}`}
            k={`${f.name} ${p.from}${p.from === 1 ? 'st' : p.from === 2 ? 'nd' : p.from === 3 ? 'rd' : 'th'}`}
            v={`${p.points ? `${p.points} pts · ` : ''}${money(p.cash)}`}
          />
        )),
      )}
      <Line k="Assumption" v="Seeding comes from the standings going into the final, so the flight ranges have to cover every player or someone finishes in no flight and is paid nothing." />
      <Line k="Assumption" v="A hole with no score posts the cap rather than being skipped, so that not finishing can never beat finishing." />
    </>
  )
}


/**
 * Every game the engine can score, in the order they would be offered.
 *
 * Written against the KINDS rather than the presets, because this list's one
 * job is to stay in step with the scorer: a format the engine gains shows up
 * here by failing the exhaustiveness check below, and one it loses stops
 * compiling. Mechanics only, no money — payouts are set in Setup when a game
 * is actually scheduled, and this page's promise is that every number on it is
 * one the scorer uses.
 */
const GAME_SHAPES: { kind: Match['config']['kind']; name: string; how: string }[] = [
  {
    kind: 'individualMatchPlay',
    name: 'Singles',
    how: 'Two men, hole by hole — the lower net takes each hole, ties halved, and the match ends when the lead exceeds the holes left.',
  },
  {
    kind: 'teamFourBall',
    name: 'Four-ball',
    how: 'Two a side, each man plays his own ball, and the side posts its lower net. Best ball wins the hole, ties halved.',
  },
  {
    kind: 'teamHighLow',
    name: 'High / Low',
    how: 'Two a side, both balls count: the low balls are compared for a point and the high balls for another, every hole. A side can win one and lose the other — carrying your partner is the whole game.',
  },
  {
    kind: 'teamCombinedNet',
    name: 'Combined Net',
    how: 'Two a side, both partners’ net scores added into one team ball. The lower total wins the hole, ties halved — a blow-up hole cannot be picked up by a partner.',
  },
  {
    kind: 'quota',
    name: 'Quota',
    how: 'No opponent — a points target built from your handicap, and a ladder that pays for eagles down to bogeys. Beat your number by more than the others beat theirs. Rankable against your foursome or against the whole field.',
  },
  {
    kind: 'strokeNet',
    name: 'Stroke play (net)',
    how: 'Count everything, subtract your allowance once at the end, lowest wins. Flightable off the standings, which is how the championship runs.',
  },
]

export function Rules({
  doc,
  header,
}: {
  doc: Tournament
  /** The mode toggle, rendered under this screen's own masthead. */
  header?: React.ReactNode
}) {
  /*
   * Counting matches, not matches. A week whose only round was added for the
   * fun of it has nothing to describe here — and the page's own promise is
   * that every number on it is one the scorer uses for money.
   */
  const scored = matchesInPlayOrder(doc).filter(matchCounts)
  if (scored.length === 0) {
    return (
      <>
        <Bar title="How it is scored" />
        {header}
        <Empty>
          {doc.matches.length === 0
            ? 'Nothing scheduled yet.'
            : 'Nothing scheduled that counts — the only rounds here are being played for fun.'}
        </Empty>
      </>
    )
  }

  const tb = doc.settings.standingsTiebreak?.steps ?? []
  /*
   * BOTH chains, and seeding first.
   *
   * This screen showed only the standings chain, under the single heading "Tie
   * chain", while telling the group that "eagles, birdies, pars and bogeys are
   * counted". The seeding chain is a different list and it is the one worth a
   * thousand dollars — it decides the championship flights — so describing the
   * other one and implying it applied to both was the wrong way round.
   */
  const seed = doc.settings.seedingTiebreak?.steps ?? []
  const both = [...tb, ...seed]
  const fieldSize = doc.players.filter((p) => p.active).length
  const chain = (steps: typeof tb): string =>
    steps.length ? steps.map((x) => (x.kind === 'countOf' ? x.what : x.kind)).join(' → ') : 'Not configured.'

  return (
    <>
      <Bar
        kicker={doc.name}
        title="How it is scored"
        sub="Read from the live settings, not written out by hand"
      />
      {header}

      <p className="px-4 py-3 text-[13px] text-mute leading-snug border-b border-rule">
        Every number below is the one the scorer actually uses. Change a payout in Setup
        and it changes here. Where the info sheet left something open, the reading we
        took is called out as an assumption.
      </p>

      {/*
        The rules of the GRASS, before the rules of the arithmetic. These are
        the two questions actually asked standing over a ball, they apply to
        the whole week, and they are prose because no config object decides
        them — with one exception: which games hole everything out IS derived,
        per match below, from the ranking scope.
      */}
      <SectionRule>Ground rules</SectionRule>
      <div className="px-4 pb-3">
        <Line
          k="Everything lateral"
          v="Every ball is played as a lateral — there is no out of bounds and no going back to the tee. Wherever it was lost or crossed out of play: drop within two clubs of that spot, add one stroke, play on."
        />
        <Line
          k="Gimmes"
          v="The length of putt given is the group’s call — except in the games against the whole field, where everything is holed out. Each game below says which it is."
        />
      </div>

      {/*
        The rules of the TOURNAMENT. A round added for the fun of it carries a
        format and therefore a ladder, but nobody is playing for that ladder,
        and this page opens by promising that every number on it is one the
        scorer actually uses.
      */}
      {scored.map((m) => {
          const round = doc.rounds.find((r) => r.id === m.roundId)
          const course = doc.courses.find((c) => c.id === round?.courseId)
          const holes = round && course ? holesFor(course, round.nine).length : 0
          const groups = doc.groups.filter((g) => g.roundId === round?.id)
          return (
            <div key={m.id}>
              <SectionRule>{matchNameIn(doc, m)}</SectionRule>
              {round && (
                <p className="px-4 pt-1 pb-1 text-[13px] text-mute">
                  {/*
                    Derived, not the stored label. This line sits directly under
                    the derived match name, so a label left behind by an edit
                    made the same screen say "Match 3 · Links" over "Heath".
                  */}
                  {roundDay(round.date)} · {roundName(round, course)} · {holes} holes
                </p>
              )}
              <div className="px-4 pb-3">
                {/*
                  The horn, on the record. A called round settles real money on
                  a partial card, and the page that promises every number the
                  scorer uses has to say that this one was cut short and how
                  the cut was handled.
                */}
                {m.calledAt && (
                  <Line
                    k="Called"
                    v={
                      m.config.kind === 'quota'
                        ? 'Ended early on darkness. Each man is measured against his target prorated to the holes he played.'
                        : 'Ended early on darkness. Every contest stands as it was — the side that was up won, square was halved.'
                    }
                  />
                )}
                <MatchRules
                  match={m}
                  holes={holes}
                  fieldSize={fieldSize}
                  /* A foursome is usually four and is not guaranteed to be. */
                  groupSize={groups[0]?.playerIds.length ?? 4}
                />
              </div>
            </div>
          )
        })}

      <SectionRule>Standings &amp; ties</SectionRule>
      <div className="px-4 pb-3">
        <Line
          k="Points"
          v="Every match pays points towards the seeding for the final. Cash is separate and does not affect seeding."
        />
        <Line k="Seeding ties" v={chain(seed)} />
        <Line k="Standings ties" v={chain(tb)} />
        <Line
          k="Counters"
          v={`Counted off ${doc.settings.tiebreakCounterBasis}. Counting them net would favour higher handicaps in every tie.`}
        />
        {/*
          This said flips were "recorded once and kept", which is not true: the
          tiebreaks table is empty after a fully scored week, because the write
          path has never run. What IS true is the part that matters — the ordering
          is deterministic, seeded from the tournament's own rngSeed and proven
          stable under reordered input, so nothing reshuffles between refreshes.
          The exposure was never instability, it was this sentence.
        */}
        <Line
          k="Coin flips"
          v="Worked out from the tournament's own fixed seed, so the same tie always resolves the same way and the board never reshuffles itself between refreshes."
        />
        {/*
          The sheet breaks a standings tie on where you finished in a particular
          round. Whether that step is in the chain is a fact about the settings,
          so it is read rather than remembered — the previous version of this
          line named "round 5", which stopped being the quota round the moment
          the tee sheet moved.
        */}
        {!both.some((x) => x.kind === 'placeInRound') && (
          <Line
            k="Open question"
            v="The sheet also breaks a tie on where you finished in a given round. That step is not in either chain above, so it is not being applied."
          />
        )}
      </div>

      {/*
        The games nobody scheduled, for the dinner-table conversation about
        adding one. Diffed against the counting matches on every render, so
        assigning a game removes it from here the moment Setup saves — and a
        week playing everything says nothing at all.
      */}
      {(() => {
        const played = new Set(scored.map((m) => m.config.kind))
        const idle = GAME_SHAPES.filter((g) => !played.has(g.kind))
        if (idle.length === 0) return null
        return (
          <>
            <SectionRule>Not on the schedule</SectionRule>
            <p className="px-4 pt-1 pb-1 text-[13px] text-mute leading-snug">
              The scorer already knows these games — they are just not assigned to any
              round. Fancy one? It is a Setup change, and the payouts get set there.
            </p>
            <div className="px-4 pb-3">
              {idle.map((g) => (
                <Line key={g.kind} k={g.name} v={g.how} />
              ))}
            </div>
          </>
        )
      })()}

      {/*
        The bookie's sheet, quietly at the foot of the rules — Scott's call:
        The week is busy enough, and a man reading the rules is already in the
        mood for the fine print. A plain link to the standalone /book page
        (the service worker deliberately bypasses it), opened in its own view
        so the app is still where he left it when he comes back.
      */}
      <SectionRule>The book</SectionRule>
      <a
        href="/book"
        target="_blank"
        rel="noopener"
        className="bc-tap bc-label w-full py-3 px-4 flex items-center justify-between border-b border-rule"
      >
        <span>The bookie&rsquo;s sheet</span>
        <span className="text-mute text-[13px] normal-case tracking-normal">
          the field priced, and how the book works ›
        </span>
      </a>

      <p className="px-4 py-6 text-[13px] text-mute leading-snug">
        Only gross strokes are stored. Net scores, pops, points, match status and every
        leaderboard number are worked out on read, so correcting a handicap or a stroke
        index repairs the whole week rather than leaving stale numbers behind.
      </p>
    </>
  )
}
